/**
 * A mark for each amenity, and for the three numbers every listing leads with.
 *
 * **These are what a card is read by.** Nobody reads "2 Beds · 2 Baths · 3 Toilets" as words at a
 * glance — they count the pictograms and move on. Every competitor in this market draws them, and
 * a card without them reads as a database row rather than a home.
 *
 * **Distinct silhouettes, not one shape with different squiggles.** A generator and a solar panel
 * are both boxes; a borehole and a water heater are both cylinders. Each one here is drawn so that
 * its outline alone is different from the others at 18px, because that is the size they are used
 * at and colour is not available to tell them apart.
 *
 * Hand-drawn on the same 24 grid at 1.6 stroke as the rest of the icon set, and free of any
 * dependency — the listing site draws the same marks from its own copy, and a shared package is a
 * better home for both than a fork. See the note in the handoff.
 */

import type { ReactElement, SVGProps } from "react";

type MarkProps = SVGProps<SVGSVGElement> & { size?: number };

function Mark({ size = 18, children, ...rest }: MarkProps) {
  return (
    <svg
      width={size}
      height={size}
      viewBox="0 0 24 24"
      fill="none"
      stroke="currentColor"
      strokeWidth={1.6}
      strokeLinecap="round"
      strokeLinejoin="round"
      aria-hidden="true"
      {...rest}
    >
      {children}
    </svg>
  );
}

/* ------------------------------------------------- the three counted things */

/** A bed, seen from the side: headboard, mattress, one pillow. */
export const IconBed = (props: MarkProps) => (
  <Mark {...props}>
    <path d="M3 18v-7a1 1 0 0 1 1-1h16a1 1 0 0 1 1 1v7" />
    <path d="M3 14h18" />
    <path d="M7 10V8a1 1 0 0 1 1-1h3a1 1 0 0 1 1 1v2" />
    <path d="M3 18h18" />
  </Mark>
);

/** A bath: a tub on two feet with a tap over it. */
export const IconBath = (props: MarkProps) => (
  <Mark {...props}>
    <path d="M3 12h18v3a4 4 0 0 1-4 4H7a4 4 0 0 1-4-4z" />
    <path d="M6 12V6a2 2 0 0 1 4 0" />
    <path d="M6 19v2M18 19v2" />
  </Mark>
);

/** A toilet: cistern, pan, and the pedestal. Counted separately from the bath in this market. */
export const IconToilet = (props: MarkProps) => (
  <Mark {...props}>
    <path d="M7 3h4v5H7z" />
    <path d="M6 8h11v3a5 5 0 0 1-5 5H9l-1 5" />
    <path d="M8 21h6" />
  </Mark>
);

/** Floor area — a rectangle with a corner arrow, the way a plan marks a dimension. */
export const IconArea = (props: MarkProps) => (
  <Mark {...props}>
    <rect x="3" y="3" width="18" height="18" rx="2" />
    <path d="M8 16 16 8M16 13V8h-5" />
  </Mark>
);

/* -------------------------------------------------------------- the things */

const IconBorehole = (props: MarkProps) => (
  <Mark {...props}>
    <path d="M12 21c-3 0-5-2-5-4.5S12 3 12 3s5 11 5 13.5S15 21 12 21z" />
  </Mark>
);

const IconGenerator = (props: MarkProps) => (
  <Mark {...props}>
    <rect x="3" y="8" width="18" height="11" rx="2" />
    <path d="M7 8V6a2 2 0 0 1 2-2h6a2 2 0 0 1 2 2v2" />
    <path d="m13 11-3 4h4l-3 4" />
  </Mark>
);

const IconSecurity = (props: MarkProps) => (
  <Mark {...props}>
    <path d="M12 3 5 6v6c0 4.5 3 7.5 7 9 4-1.5 7-4.5 7-9V6z" />
    <path d="m9 12 2 2 4-4" />
  </Mark>
);

const IconGated = (props: MarkProps) => (
  <Mark {...props}>
    <path d="M3 20V9l4-3 4 3v11" />
    <path d="M13 20V9l4-3 4 3v11" />
    <path d="M2 20h20" />
    <path d="M7 13v3M17 13v3" />
  </Mark>
);

const IconParking = (props: MarkProps) => (
  <Mark {...props}>
    <path d="M3 16v-3l2-5a2 2 0 0 1 2-1.4h10A2 2 0 0 1 19 8l2 5v3" />
    <path d="M3 16h18v3h-3v-3M6 19H3z" />
    <circle cx="7" cy="16" r="1.2" />
    <circle cx="17" cy="16" r="1.2" />
  </Mark>
);

/** Boys' quarters — the small building behind the main one. */
const IconBoysQuarters = (props: MarkProps) => (
  <Mark {...props}>
    <path d="M3 21V10l5-4 5 4v11" />
    <path d="M13 21v-7l4-3 4 3v7" />
    <path d="M2 21h20" />
    <path d="M7 21v-4h2v4" />
  </Mark>
);

const IconEnSuite = (props: MarkProps) => (
  <Mark {...props}>
    <rect x="3" y="3" width="18" height="18" rx="2" />
    <path d="M14 3v18" />
    <path d="M17 9h2M17 15a2 2 0 0 0 4 0" />
  </Mark>
);

const IconKitchen = (props: MarkProps) => (
  <Mark {...props}>
    <rect x="3" y="4" width="18" height="16" rx="2" />
    <path d="M3 12h18" />
    <circle cx="8" cy="8" r="1" />
    <circle cx="12" cy="8" r="1" />
    <path d="M8 15v2M14 15h4" />
  </Mark>
);

const IconWardrobe = (props: MarkProps) => (
  <Mark {...props}>
    <rect x="4" y="3" width="16" height="18" rx="1.5" />
    <path d="M12 3v18" />
    <path d="M10 11v2M14 11v2" />
  </Mark>
);

const IconMeter = (props: MarkProps) => (
  <Mark {...props}>
    <rect x="4" y="3" width="16" height="18" rx="2" />
    <circle cx="12" cy="10" r="3.5" />
    <path d="M12 10V8M8 17h8" />
  </Mark>
);

const IconAircon = (props: MarkProps) => (
  <Mark {...props}>
    <rect x="3" y="5" width="18" height="7" rx="2" />
    <path d="M7 9h10" />
    <path d="M8 15c0 1.5 1 1.5 1 3M12 15c0 1.5 1 1.5 1 3M16 15c0 1.5 1 1.5 1 3" />
  </Mark>
);

const IconFurnished = (props: MarkProps) => (
  <Mark {...props}>
    <path d="M4 17v-6a2 2 0 0 1 2-2h12a2 2 0 0 1 2 2v6" />
    <path d="M3 17h18v3H3z" />
    <path d="M7 9V7a2 2 0 0 1 2-2h6a2 2 0 0 1 2 2v2" />
  </Mark>
);

const IconServiced = (props: MarkProps) => (
  <Mark {...props}>
    <path d="M4 20h16" />
    <path d="M12 4v3" />
    <path d="M5 17a7 7 0 0 1 14 0z" />
  </Mark>
);

const IconNew = (props: MarkProps) => (
  <Mark {...props}>
    <path d="M12 3l2.2 4.8 5.3.6-4 3.6 1.1 5.2L12 14.6 7.4 17.2l1.1-5.2-4-3.6 5.3-.6z" />
  </Mark>
);

const IconCeiling = (props: MarkProps) => (
  <Mark {...props}>
    <path d="M3 4h18" />
    <path d="M5 4v4h14V4" />
    <path d="M8 12h8M7 16h10M6 20h12" />
  </Mark>
);

const IconTiled = (props: MarkProps) => (
  <Mark {...props}>
    <rect x="3" y="3" width="18" height="18" rx="1.5" />
    <path d="M3 9h18M3 15h18M9 3v18M15 3v18" />
  </Mark>
);

const IconWaterHeater = (props: MarkProps) => (
  <Mark {...props}>
    <rect x="6" y="3" width="12" height="14" rx="4" />
    <path d="M10 21c0-1.5 1-1.5 1-3M14 21c0-1.5 1-1.5 1-3" />
    <path d="M12 8v4" />
  </Mark>
);

const IconBalcony = (props: MarkProps) => (
  <Mark {...props}>
    <path d="M4 4h16" />
    <path d="M6 4v8M18 4v8" />
    <path d="M3 12h18" />
    <path d="M6 12v8M12 12v8M18 12v8M3 20h18" />
  </Mark>
);

const IconGarden = (props: MarkProps) => (
  <Mark {...props}>
    <path d="M12 21v-7" />
    <path d="M12 14c-4 0-6-2.5-6-6 4 0 6 2.5 6 6z" />
    <path d="M12 14c0-3.5 2-6 6-6 0 3.5-2 6-6 6z" />
    <path d="M4 21h16" />
  </Mark>
);

const IconPool = (props: MarkProps) => (
  <Mark {...props}>
    <path d="M3 17c1.5 0 1.5 1.5 3 1.5S7.5 17 9 17s1.5 1.5 3 1.5 1.5-1.5 3-1.5 1.5 1.5 3 1.5 1.5-1.5 3-1.5" />
    <path d="M8 15V6a2 2 0 0 1 4 0v9M16 15V6" />
    <path d="M8 10h4" />
  </Mark>
);

const IconGym = (props: MarkProps) => (
  <Mark {...props}>
    <path d="M3 9v6M6 7v10M18 7v10M21 9v6" />
    <path d="M6 12h12" />
  </Mark>
);

const IconCctv = (props: MarkProps) => (
  <Mark {...props}>
    <path d="m3 8 14-4 2 5-14 4z" />
    <path d="m6 12 1.5 4" />
    <path d="M12 20a4 4 0 0 0-4-4" />
    <path d="M18 9l3 1" />
  </Mark>
);

const IconLift = (props: MarkProps) => (
  <Mark {...props}>
    <rect x="4" y="3" width="16" height="18" rx="2" />
    <path d="M12 3v18" />
    <path d="m8 11 1.5-2L11 11M13 13l1.5 2 1.5-2" />
  </Mark>
);

const IconSolar = (props: MarkProps) => (
  <Mark {...props}>
    <path d="m4 15 2-8h12l2 8z" />
    <path d="M5.5 11h13M11 7v8" />
    <path d="M12 15v3M8 21h8" />
  </Mark>
);

const IconStudy = (props: MarkProps) => (
  <Mark {...props}>
    <path d="M4 5h7v14H4z" />
    <path d="M13 5h7v14h-7z" />
    <path d="M6 9h3M15 9h3" />
  </Mark>
);

const IconCinema = (props: MarkProps) => (
  <Mark {...props}>
    <rect x="3" y="6" width="12" height="12" rx="2" />
    <path d="m15 11 6-3v8l-6-3z" />
    <circle cx="7" cy="10" r="1" />
  </Mark>
);

const IconWifi = (props: MarkProps) => (
  <Mark {...props}>
    <path d="M2 8.5a15 15 0 0 1 20 0" />
    <path d="M5.5 12.5a10 10 0 0 1 13 0" />
    <path d="M9 16.5a5 5 0 0 1 6 0" />
    <circle cx="12" cy="20" r="0.6" />
  </Mark>
);

const IconPets = (props: MarkProps) => (
  <Mark {...props}>
    <circle cx="7" cy="9" r="1.8" />
    <circle cx="12" cy="7" r="1.8" />
    <circle cx="17" cy="9" r="1.8" />
    <path d="M12 12c-3 0-5 2-5 4.5S9 20 12 20s5-1 5-3.5S15 12 12 12z" />
  </Mark>
);

const IconStepFree = (props: MarkProps) => (
  <Mark {...props}>
    <circle cx="12" cy="5" r="2" />
    <path d="M9 9h6l-1 4h-4z" />
    <path d="M4 20h5l3-7M14 13l3 3v4" />
  </Mark>
);

const MARKS: Record<string, (props: MarkProps) => ReactElement> = {
  borehole: IconBorehole,
  generator: IconGenerator,
  security: IconSecurity,
  gated: IconGated,
  parking: IconParking,
  boys_quarters: IconBoysQuarters,
  en_suite: IconEnSuite,
  fitted_kitchen: IconKitchen,
  wardrobes: IconWardrobe,
  prepaid_meter: IconMeter,
  air_conditioning: IconAircon,
  furnished: IconFurnished,
  serviced: IconServiced,
  newly_built: IconNew,
  pop_ceiling: IconCeiling,
  tiled: IconTiled,
  water_heater: IconWaterHeater,
  balcony: IconBalcony,
  garden: IconGarden,
  swimming_pool: IconPool,
  gym: IconGym,
  cctv: IconCctv,
  lift: IconLift,
  solar: IconSolar,
  study: IconStudy,
  cinema: IconCinema,
  wifi: IconWifi,
  pet_friendly: IconPets,
  wheelchair: IconStepFree,
};

/** A dot, for an amenity added to the list before anybody drew it. */
const IconUnknown = (props: MarkProps) => (
  <Mark {...props}>
    <circle cx="12" cy="12" r="3.5" />
  </Mark>
);

export function AmenityIcon({ amenity, size = 18, className = "" }: { amenity: string; size?: number; className?: string }) {
  const Component = MARKS[amenity] ?? IconUnknown;
  return <Component size={size} className={className} />;
}
